import { ethers } from 'ethers';
import { LEVER_SWAP_CONFIG } from './swapConfig';
import { ERC20_ABI, safeNormalize } from './swapUtils';

function shortAddress(address) {
  if (!address) return '—';
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}

function uniqueAddresses(addresses) {
  const seen = new Set();
  return addresses
  .filter(Boolean)
  .map((address) => safeNormalize(address))
  .filter((address) => {
    const key = address.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function getStarTaxRevokeTargets() {
  return uniqueAddresses([
  LEVER_SWAP_CONFIG.PERMIT2_ADDRESS,
  LEVER_SWAP_CONFIG.ROUTER_ADDRESS]
  ).map((spender) => ({
    tokenAddress: safeNormalize(LEVER_SWAP_CONFIG.PR_ADDRESS),
    spender,
    label: `★TAX → ${shortAddress(spender)}`
  }));
}

export async function revokeStarTaxAllowances({ signer, ownerAddress, onStep } = {}) {
  if (!signer) throw new Error('Wallet signer is required to revoke allowances.');
  if (!ownerAddress) throw new Error('Wallet address is required to revoke allowances.');

  const network = await signer.provider.getNetwork();
  if (Number(network?.chainId) !== Number(LEVER_SWAP_CONFIG.CHAIN_ID)) {
    const err = new Error(`Wrong network. Switch to ${LEVER_SWAP_CONFIG.CHAIN_NAME} before revoking allowances.`);
    err.code = 'WRONG_NETWORK';
    throw err;
  }

  const tokenAddress = safeNormalize(LEVER_SWAP_CONFIG.PR_ADDRESS);
  const token = new ethers.Contract(tokenAddress, ERC20_ABI, signer);
  const targets = getStarTaxRevokeTargets();
  const revoked = [];
  const skipped = [];

  for (const target of targets) {
    const spender = safeNormalize(target.spender);
    onStep?.({ step: 'checking_allowance', tokenAddress, spender, label: target.label });

    const allowance = await token.allowance(safeNormalize(ownerAddress), spender);
    const allowanceRaw = allowance.toString();

    if (allowance === BigInt(0)) {
      skipped.push({ ...target, allowanceRaw, reason: 'zero_allowance' });
      onStep?.({ step: 'allowance_already_zero', tokenAddress, spender, label: target.label, allowanceRaw });
      continue;
    }

    onStep?.({ step: 'revoke_requested', tokenAddress, spender, label: target.label, allowanceRaw });
    const tx = await signer.sendTransaction({to:tokenAddress,data:token.interface.encodeFunctionData('approve',[spender,0])});
    onStep?.({ step: 'revoke_submitted', tokenAddress, spender, label: target.label, txHash: tx.hash, allowanceRaw });
    const receipt = await tx.wait();

    revoked.push({
      ...target,
      allowanceRaw,
      txHash: tx.hash,
      blockNumber: receipt?.blockNumber || null,
      status: receipt?.status ?? null
    });
    onStep?.({ step: 'revoke_confirmed', tokenAddress, spender, label: target.label, txHash: tx.hash, receipt });
  }

  return {
    tokenAddress,
    ownerAddress: safeNormalize(ownerAddress),
    targets,
    revoked,
    skipped,
    revokedCount: revoked.length,
    skippedCount: skipped.length,
    hadActiveAllowances: revoked.length > 0
  };
}
